<template>
  <div
    id="withdrawn-tab"
    tabindex="0"
    role="tabpanel"
    aria-labelledby="withdrawn"
    class="focus:outline-none"
  >
    <Teleport v-if="parentMounted" to="#csvBtn">
      <a
        :class="`${secondaryColorClass}`"
        class="items-center hidden md:flex font-semibold"
        href="#"
        @click="csvExport()"
      >
        <span class="flex-none">
          <i class="mdi mdi-download"></i>
        </span>
        <span class="ml-2">CSV</span>
      </a>
    </Teleport>
    <modal
      v-if="showModal"
      :id="'breakdown'"
      :title="'Total Program Price Breakdown'"
      @closemodal="resetStudent"
    >
      <template #modal-content>
        <PriceBreakDown @close="resetStudent" />
      </template>
    </modal>

    <Teleport v-if="parentMounted" to="#searchbox">
      <label class="blocktext-sm">
        <span class="font-semibold text-gray-600 flex justify-between sr-only">
          <span>Search</span>
        </span>
        <input
          v-model="tableData.search"
          class="form-input min-h-10 block w-full"
          type="text"
          placeholder="Search"
          @input="getStudents(searchUrl)"
        />
      </label>
    </Teleport>

    <noRecords v-if="!loading & (students.length == 0)" />
    <datatable
      v-else
      :columns="columns"
      :sort-key="sortKey"
      :sort-orders="sortOrders"
      role="region"
      aria-label="Withdrawn Results"
      @sort="sortBy"
    >
      <tr v-if="loading">
        <td class="card-data">
          <div class="w-full md:w-columnfixed-md3">Loading Please Wait...</div>
        </td>
        <td class="card-data">
          <div class="w-full md:w-columnfixed-sm"></div>
        </td>
        <td class="card-data">
          <div class="w-full md:w-columnfixed-md"></div>
        </td>
        <td class="card-data">
          <div class="w-full md:w-columnfixed-md"></div>
        </td>
        <td class="card-data">
          <div class="w-full md:w-columnfixed-sm"></div>
        </td>
        <td class="card-data">
          <div class="w-full md:w-columnfixed-sm"></div>
        </td>
        <td class="card-data">
          <div class="w-full md:w-columnfixed-ctrl"></div>
        </td>
      </tr>
      <p v-else-if="students.length == 0" class="text-red-600">
        No records found!
      </p>
      <tbody v-else>
        <tr
          v-for="(student, index) in students"
          :key="student.id + 'i' + index"
          class="card-row"
        >
          <td class="card-section">
            <div class="card-label">
              Student Name and Email
            </div>
            <div class="w-full md:w-columnfixed-md2">
              <span class="block text-sm font-semibold text-gray-700">
                {{ student.name }}
              </span>
              <span class="break-words">
                <a
                  :href="'mailto:' + student.email"
                  :class="[
                    secondaryColor
                      ? `${secondaryColorClass} focus:underline`
                      : `${secondaryColorClass} focus:underline hover:text-teal-900 focus:text-teal-900`,
                  ]"
                  >{{ student.email }}</a
                >
              </span>
            </div>
          </td>
          <td class="card-data">
            <div class="card-label">
              Major
            </div>
            <div
              class="xs:text-left xs:w-1/2 sm:w-2/3 md:w-columnfixed-sm break-words"
            >
              {{ student.major || "-" }}
            </div>
          </td>
          <td class="card-section">
            <div class="card-label">
              Program Name
            </div>
            <div class="w-full md:w-columnfixed-md break-words">
              <a
                v-if="student.url"
                :href="student.url"
                target="_blank"
                :class="[
                  secondaryColor
                    ? `${secondaryColorClass} focus:underline`
                    : `${secondaryColorClass} focus:underline hover:text-teal-900 focus:text-teal-900`,
                ]"
              >
                {{ student.program_name }}
              </a>
              <span v-else class="block">{{ student.program_name }}</span>
              <span class="block">{{ student.site }}</span>
            </div>
          </td>
          <td class="card-data">
            <div class="card-label">
              Program Dates
            </div>
            <div class="xs:text-left xs:w-1/2 sm:w-2/3 md:w-columnfixed-md">
              <datesComponent
                :id="student.id + 'r' + index"
                :dates="student.program_dates"
                :start-is-final="student.start_date_final > 0"
                :end-is-final="student.end_date_final > 0"
                :extra-styles="'width:16px;'"
              />
            </div>
          </td>
          <td class="card-data">
            <div class="card-label">
              Withdrawn Reason
            </div>
            <div
              class="xs:text-left xs:w-1/2 sm:w-2/3 md:w-columnfixed-sm break-words"
            >
              {{ student.withdrawal_reason }}
            </div>
          </td>
          <td class="card-data">
            <div class="card-label">
              Withdrawn Date
            </div>
            <div
              class="xs:text-left xs:w-1/2 sm:w-2/3 md:w-columnfixed-sm break-words"
            >
              {{ student.withdrawal_date }}
            </div>
          </td>
          <td class="card-controls">
            <dropdownComponent :row="index + 1" :tab-name="'withdrawn'">
              <template #dropdown-options="slotProps">
                <li class="border-t border-gray-200">
                  <a class="dropdown-item" :href="'mailto:' + student.email"
                    >Email Student</a
                  >
                </li>
                <li class="border-t border-gray-200">
                  <a
                    class="dropdown-item"
                    :href="`mailto:api-urs${slotProps.companyEmail}`"
                    >Email API</a
                  >
                </li>
              </template>
            </dropdownComponent>
          </td>
        </tr>
      </tbody>
    </datatable>
    <pagination
      :pagination="pagination"
      @prev="getStudents(pagination.prevPageUrl)"
      @next="getStudents(pagination.nextPageUrl)"
    />
  </div>
</template>

<script>
import datatables from "../DataTables";
import pagination from "../Pagination";
import noRecords from "../NoRecords";
import { tabsMixin } from "@/mixins/tabsMixins";
import programDates from "../programDates";
import dropdownComponent from "../../SiteComponents/my-students/misc/dropdown";
import PriceBreakDown from "@/university/components/SiteComponents/priceBreakDown";
import modal from "@/university/components/modal";
import { APICompany } from "../../../../constants";

export default {
  components: {
    datatable: datatables,
    pagination: pagination,
    noRecords: noRecords,
    dropdownComponent: dropdownComponent,
    datesComponent: programDates,
    PriceBreakDown,
    modal,
  },
  mixins: [tabsMixin],
  props: {
    parentMounted: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    let sortOrders = {};
    //columns headers for this table, must match a row from columnsWhiteList->row->name in tabMixin.js
    let tabColumns = [
      "nameEmail",
      "major",
      "programName",
      "programDates",
      "withdrawnReason",
      "withdrawnDate",
    ];
    tabColumns.forEach((column) => {
      sortOrders[column] = 0;
    });

    return {
      searchUrl:
        this.trailingSlash(import.meta.env.VITE_ENROLLMENT_SERVICE_ENDPOINT) +
        "students",
      id: "",
      loading: true,
      students: [],
      tabColumns: tabColumns,
      sortKey: "col1",
      sortOrders: sortOrders,
      perPage: ["10", "20", "30"],
      tableData: {
        draw: 0,
        length: 25,
        search: "",
        column: 0,
        dir: "asc",
        status: ["Withdrawn"],
        year: new Date().getFullYear(),
        current_year: new Date().getFullYear(),
        filters: {
          city: "",
          sessions: [],
          majors: [],
          approvalStatus: "",
        },
        APICompany,
      },
    };
  },
  created() {
    this.getStudents(this.searchUrl, true);
  },
  methods: {
    setYear(event) {
      this.tableData.year = event.target.value;
      this.getStudents(this.searchUrl);
    },
    prepDataForCsvExport(csvData) {
      return csvData.map((item) => ({
        Name: item.name,
        Email: item.email,
        Major: item.major,
        Program_Name: item.program_name,
        withdrawn_Reason: "___",
      }));
    },
  },
};
</script>
